import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Container, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { FaRobot } from "react-icons/fa";
import Cookies from "universal-cookie";
import axios from "axios";

const COGNITO_CLIENT_ID = "434nnngm1kfvo0fm4umg3v8bud";
const COGNITO_REGION = "us-east-2";

const ERROR_MESSAGES = {
  NotAuthorizedException: "Incorrect email or password.",
  UserNotFoundException: "No account found with that email.",
  UserNotConfirmedException: "Please verify your email before logging in.",
  PasswordResetRequiredException: "A password reset is required. Please contact support.",
  NEW_PASSWORD_REQUIRED: "You must set a new password. Please contact support.",
};

const Login = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await axios.post(
        `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`,
        {
          AuthFlow: "USER_PASSWORD_AUTH",
          ClientId: COGNITO_CLIENT_ID,
          AuthParameters: {
            USERNAME: username.trim(),
            PASSWORD: password,
          },
        },
        {
          headers: {
            "Content-Type": "application/x-amz-json-1.1",
            "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
          },
        }
      );

      const { ChallengeName, AuthenticationResult } = response.data;

      if (ChallengeName) {
        setError(ERROR_MESSAGES[ChallengeName] ?? `Auth challenge required: ${ChallengeName}`);
        return;
      }

      const { AccessToken } = AuthenticationResult;
      const cookies = new Cookies();
      cookies.set("jwt", AccessToken, { path: "/", secure: true });
      sessionStorage.setItem("jwt", AccessToken);

      navigate("/app");
    } catch (err) {
      const code = err.response?.data?.__type?.split("#").pop();
      setError(ERROR_MESSAGES[code] ?? "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
      <Card style={{ width: "100%", maxWidth: 420 }} className="p-4 shadow-sm">
        <div className="text-center mb-4">
          <FaRobot size={36} className="text-primary mb-2" />
          <h4 className="fw-bold mb-0">Sign in to RoboVest</h4>
          <p className="text-muted small mt-1">Enter your credentials to continue</p>
        </div>

        {error && (
          <Alert variant="danger" className="py-2 small">
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="loginEmail">
            <Form.Label>Username or Email</Form.Label>
            <Form.Control
              type="text"
              placeholder="Username or email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
          </Form.Group>

          <Form.Group className="mb-4" controlId="loginPassword">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Form.Group>

          <Button
            type="submit"
            variant="primary"
            className="w-100"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Signing in…
              </>
            ) : (
              "Sign In"
            )}
          </Button>
        </Form>

        <p className="text-center text-muted small mt-4 mb-0">
          Don't have an account?{" "}
          <Link to="/signup" className="text-primary text-decoration-none">
            Sign up
          </Link>
        </p>
      </Card>
    </Container>
  );
};

export default Login;
